/**
 * ATTSYNC (Attribute Sync) mutation — Phase 2.5.
 *
 * AutoCAD ATTSYNC synchronises ATTRIB rows on existing INSERTs with
 * the current ATTDEF template of the referenced block:
 *   - New ATTDEFs       → create a fresh ATTRIB row initialised from
 *                         the defaultValue (non-constant ATTDEFs only).
 *   - Deleted ATTDEFs   → drop the orphan ATTRIB rows.
 *   - Constant ATTDEFs  → remove the corresponding ATTRIB row (the
 *                         constant value is carried by the template
 *                         itself; storing it redundantly on every
 *                         INSERT would desynchronise with AutoCAD).
 *   - Property changes  → the current model stores ATTDEF properties
 *                         on the template alone — render-time lookup
 *                         applies user overrides (overrideHeight /
 *                         Rotation / Position / Style / invisible),
 *                         so `ATTSYNC` is a no-op for non-overridden
 *                         properties. Overrides are preserved.
 *
 * Input may narrow the scope to a subset of INSERT ids; otherwise the
 * mutation acts on every INSERT that references the block.
 */

import type {
  AttdefEntity,
  BlockInsertAttribute,
  BlockInsertEntity,
  DrawingDocument,
} from "@dxf-ai-editor/drawing-model";

import { requireBlock, withInsertsOfBlock } from "./mutationHelpers";

export interface AttsyncInput {
  blockName: string;
  drawingDocument: DrawingDocument;
  /** Restrict ATTSYNC to these INSERT ids; undefined = every INSERT. */
  targetInsertIds?: string[];
}

export interface AttsyncStats {
  insertsUpdated: number;
  attribsAdded: number;
  attribsRemoved: number;
  attribsSynced: number;
}

export interface AttsyncResult {
  updatedDocument: DrawingDocument;
  stats: AttsyncStats;
  summary: string;
}

function buildTemplateAttribMap(
  attdefs: AttdefEntity[],
): Map<string, AttdefEntity> {
  const map = new Map<string, AttdefEntity>();
  for (const a of attdefs) {
    map.set(a.tag, a);
  }
  return map;
}

/**
 * Apply ATTSYNC to a single INSERT given the current ATTDEF template.
 * Returns `{ attributes, stats }` for caller aggregation.
 */
function syncAttributesForInsert(
  insert: BlockInsertEntity,
  templateMap: Map<string, AttdefEntity>,
  nonConstantTemplate: AttdefEntity[],
): {
  attributes: BlockInsertAttribute[];
  added: number;
  removed: number;
  synced: number;
} {
  const existingByTag = new Map<string, BlockInsertAttribute>();
  for (const a of insert.attributes) {
    existingByTag.set(a.tag, a);
  }

  let added = 0;
  let removed = 0;
  let synced = 0;

  // Start with every non-constant ATTDEF tag in the template. Each
  // produces an ATTRIB row (reusing the existing one when present).
  const next: BlockInsertAttribute[] = [];
  for (const attdef of nonConstantTemplate) {
    const existing = existingByTag.get(attdef.tag);
    if (existing) {
      // Preserve user-authored value and any overrides. Sync marker
      // bumps when an ATTRIB would otherwise be dropped / recreated.
      next.push(existing);
      synced += 1;
    } else {
      // Brand-new ATTRIB — seed from ATTDEF template.
      next.push({
        tag: attdef.tag,
        value: attdef.defaultValue,
        handle: "",
      });
      added += 1;
    }
  }

  // Count removals: existing tags that are no longer present as a
  // non-constant ATTDEF (orphan or flipped-to-constant).
  for (const a of insert.attributes) {
    const attdef = templateMap.get(a.tag);
    if (!attdef || attdef.flags.constant) {
      removed += 1;
    }
  }

  return {
    attributes: next,
    added,
    removed,
    synced,
  };
}

export function attsyncMutation(input: AttsyncInput): AttsyncResult {
  const { blockName, drawingDocument, targetInsertIds } = input;
  const blockDef = requireBlock(drawingDocument, blockName);

  const attdefs = blockDef.entities.filter(
    (e): e is AttdefEntity => e.type === "Attdef",
  );
  const templateMap = buildTemplateAttribMap(attdefs);
  const nonConstantTemplate = attdefs.filter((a) => !a.flags.constant);
  const targetSet = targetInsertIds ? new Set(targetInsertIds) : null;

  let attribsAdded = 0;
  let attribsRemoved = 0;
  let attribsSynced = 0;

  const insertWalk = withInsertsOfBlock(
    drawingDocument,
    blockName,
    (entity) => {
      if (targetSet && !targetSet.has(entity.id)) return entity;

      const sync = syncAttributesForInsert(
        entity,
        templateMap,
        nonConstantTemplate,
      );
      attribsAdded += sync.added;
      attribsRemoved += sync.removed;
      attribsSynced += sync.synced;

      const changed =
        sync.attributes.length !== entity.attributes.length ||
        sync.attributes.some(
          (a, i) =>
            a.tag !== entity.attributes[i]?.tag ||
            a.value !== entity.attributes[i]?.value,
        );
      return changed ? { ...entity, attributes: sync.attributes } : entity;
    },
  );

  return {
    updatedDocument: insertWalk.doc,
    stats: {
      insertsUpdated: insertWalk.changedCount,
      attribsAdded,
      attribsRemoved,
      attribsSynced,
    },
    summary: `ATTSYNC ${blockName}: ${insertWalk.changedCount} insert${
      insertWalk.changedCount === 1 ? "" : "s"
    } updated (+${attribsAdded}/-${attribsRemoved}/~${attribsSynced})`,
  };
}
